import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Users, Shield, UserCheck, UserX, Loader2, PlayCircle, PauseCircle, Trash2, X, FileText, KeyRound, Coins, MoreVertical, CalendarClock } from 'lucide-react';
import Table from '../components/ui/Table';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import DatePicker from '../components/ui/DatePicker';
import { formatDate } from '../utils/formatDate';
import { API_PATH, request } from '../config/api';
import { useAuth } from '../store/AuthContext';
import PageHeroHeader from '../components/ui/PageHeroHeader';
import styles from './AdminPage.module.css';

const RoleBadge = ({ role }) => {
    const map = {
        admin: { variant: 'info', label: 'Admin' },
        superadmin: { variant: 'primary', label: 'Superadmin' },
    };
    const { variant, label } = map[role] || { variant: 'neutral', label: role };
    return <Badge variant={variant} size="md" className={styles.badge}>{label}</Badge>;
};

const StatusBadge = ({ status }) => {
    const isActive = status === 'active';
    return (
        <Badge variant={isActive ? 'success' : 'error'} size="md" className={styles.badge}>
            <span className={styles.statusDot} />
            {isActive ? 'Active' : 'Expired'}
        </Badge>
    );
};

const AdminPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteModal, setDeleteModal] = useState(null);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleteError, setDeleteError] = useState('');
    const [deletingId, setDeletingId] = useState(null);
    const [actionId, setActionId] = useState(null);
    const [summaryModal, setSummaryModal] = useState(null);
    const [summaryData, setSummaryData] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [resetPwdModal, setResetPwdModal] = useState(null);
    const [resetNewPassword, setResetNewPassword] = useState('');
    const [resetConfirmPassword, setResetConfirmPassword] = useState('');
    const [resetError, setResetError] = useState('');
    const [resettingId, setResettingId] = useState(null);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState(null);
    const [expiryModal, setExpiryModal] = useState(null);
    const [expiryDate, setExpiryDate] = useState('');
    const [expiryError, setExpiryError] = useState('');
    const [settingExpiryId, setSettingExpiryId] = useState(null);
    const menuRef = useRef(null);
    const triggerRef = useRef(null);

    const isSuperadmin = user?.role === 'superadmin';

    useLayoutEffect(() => {
        if (!openMenuId || !triggerRef.current) {
            setDropdownPosition(null);
            return;
        }
        const rect = triggerRef.current.getBoundingClientRect();
        const dropdownWidth = 180;
        setDropdownPosition({
            top: rect.bottom + 4,
            left: Math.min(rect.right - dropdownWidth, window.innerWidth - dropdownWidth - 8),
        });
    }, [openMenuId]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
            setOpenMenuId(null);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchAdmins = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await request(`${API_PATH}/superadmin/admins`);
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setError(data.error || 'Failed to load admins');
                setAdmins([]);
                return;
            }
            const data = await res.json();
            setAdmins(data.admins || []);
        } catch (e) {
            setError('Network error');
            setAdmins([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isSuperadmin) {
            navigate('/dashboard', { replace: true });
            return;
        }
        fetchAdmins();
    }, [isSuperadmin, navigate, fetchAdmins]);

    const handleActivate = async (adminId) => {
        setActionId(adminId);
        try {
            const res = await request(`${API_PATH}/superadmin/admins/${adminId}/activate`, {
                method: 'PATCH',
            });
            if (res.ok) await fetchAdmins();
            else setError((await res.json().catch(() => ({}))).error || 'Failed to activate');
        } finally {
            setActionId(null);
        }
    };

    const handleDeactivate = async (adminId) => {
        setActionId(adminId);
        try {
            const res = await request(`${API_PATH}/superadmin/admins/${adminId}/deactivate`, {
                method: 'PATCH',
            });
            if (res.ok) await fetchAdmins();
            else setError((await res.json().catch(() => ({}))).error || 'Failed to deactivate');
        } finally {
            setActionId(null);
        }
    };

    const openSummaryModal = async (admin) => {
        setSummaryModal(admin);
        setSummaryData(null);
        setSummaryLoading(true);
        try {
            const res = await request(`${API_PATH}/superadmin/admins/${admin.id}/summary`);
            const data = await res.json().catch(() => ({}));
            if (res.ok) setSummaryData(data);
        } catch (e) {
            setSummaryData({ error: 'Network error' });
        } finally {
            setSummaryLoading(false);
        }
    };

    const closeSummaryModal = () => {
        setSummaryModal(null);
        setSummaryData(null);
    };

    const openResetPwdModal = (admin) => {
        setResetPwdModal(admin);
        setResetNewPassword('');
        setResetConfirmPassword('');
        setResetError('');
    };

    const closeResetPwdModal = () => {
        setResetPwdModal(null);
        setResetNewPassword('');
        setResetConfirmPassword('');
        setResetError('');
    };

    const openExpiryModal = (admin) => {
        setExpiryModal(admin);
        const exp = admin.expires_at;
        setExpiryDate(exp ? (typeof exp === 'string' ? exp.slice(0, 10) : new Date(exp).toISOString().slice(0, 10)) : '');
        setExpiryError('');
    };

    const closeExpiryModal = () => {
        setExpiryModal(null);
        setExpiryDate('');
        setExpiryError('');
    };

    const handleSetExpiry = async () => {
        if (!expiryModal) return;
        setSettingExpiryId(expiryModal.id);
        setExpiryError('');
        try {
            const res = await request(`${API_PATH}/superadmin/admins/${expiryModal.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ expiresAt: expiryDate || null }),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                closeExpiryModal();
                setOpenMenuId(null);
                await fetchAdmins();
            } else {
                setExpiryError(data.error || 'Failed to set expiry');
            }
        } catch (e) {
            setExpiryError('Network error');
        } finally {
            setSettingExpiryId(null);
        }
    };

    const handleResetPassword = async () => {
        if (!resetPwdModal) return;
        if (resetNewPassword.length < 8) {
            setResetError('Password must be at least 8 characters');
            return;
        }
        if (resetNewPassword !== resetConfirmPassword) {
            setResetError('Passwords do not match');
            return;
        }
        setResettingId(resetPwdModal.id);
        setResetError('');
        try {
            const res = await request(`${API_PATH}/superadmin/admins/${resetPwdModal.id}/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ newPassword: resetNewPassword }),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                closeResetPwdModal();
            } else {
                setResetError(data.error || 'Failed to reset password');
            }
        } catch (e) {
            setResetError('Network error');
        } finally {
            setResettingId(null);
        }
    };

    const openDeleteModal = (admin) => {
        setDeleteModal(admin);
        setDeletePassword('');
        setDeleteError('');
    };

    const closeDeleteModal = () => {
        setDeleteModal(null);
        setDeletePassword('');
        setDeleteError('');
    };

    const handleConfirmDelete = async () => {
        if (!deleteModal || !deletePassword.trim()) {
            setDeleteError('Enter your password to confirm.');
            return;
        }
        setDeletingId(deleteModal.id);
        setDeleteError('');
        try {
            const res = await request(`${API_PATH}/superadmin/admins/${deleteModal.id}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ password: deletePassword.trim() }),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                closeDeleteModal();
                await fetchAdmins();
            } else if (res.status === 401) {
                setDeleteError(data.error || 'Incorrect password.');
            } else {
                setDeleteError(data.error || 'Failed to delete.');
            }
        } catch (e) {
            setDeleteError('Network error.');
        } finally {
            setDeletingId(null);
        }
    };

    if (!isSuperadmin) return null;

    const activeCount = admins.filter((a) => a.status === 'active').length;
    const expiredCount = admins.filter((a) => a.status === 'expired').length;

    const userColumns = [
        { key: 'name', header: 'Name' },
        { key: 'email', header: 'Email' },
        {
            key: 'validation',
            header: 'Validation',
            render: (row) => (
                <div className={styles.validationCell}>
                    <span className={styles.validationRow}>
                        <span className={styles.validationLabel}>Created</span>
                        <span>{row.created_at ? formatDate(row.created_at) : '—'}</span>
                    </span>
                    <span className={styles.validationRow}>
                        <span className={styles.validationLabel}>Expires</span>
                        <span>{row.expires_at ? formatDate(row.expires_at) : 'No expiry'}</span>
                    </span>
                </div>
            ),
        },
        {
            key: 'status',
            header: 'Status',
            render: (row) => <StatusBadge status={row.status} />,
        },
        {
            key: 'coins',
            header: 'Coins',
            render: (row) => <span className={styles.coinsCell}>{row.coins ?? 0}</span>,
        },
        {
            key: 'subscription',
            header: 'Subscription',
            render: (row) => (row.subscription_end_date ? formatDate(row.subscription_end_date) : '—'),
        },
        {
            key: 'statusActions',
            header: 'Actions',
            render: (row) => (
                <div className={styles.rowActionsWrap}>
                    <button
                        ref={openMenuId === row.id ? triggerRef : null}
                        type="button"
                        className={styles.menuTrigger}
                        onClick={() => setOpenMenuId((id) => (id === row.id ? null : row.id))}
                        aria-haspopup="true"
                        aria-expanded={openMenuId === row.id}
                        aria-label="Actions"
                    >
                        <MoreVertical size={18} />
                    </button>
                </div>
            ),
        },
    ];

    return (
        <div className={styles.page}>
            <PageHeroHeader
                icon={Shield}
                title="Admin console"
                badge={<RoleBadge role={user?.role} />}
                subtitle="Review admin accounts, monitor status, and manage access, coins, and subscriptions from a single place."
                actions={<div className={styles.headerBadge}><Shield size={16} /><span>Superadmin workspace</span></div>}
            />

            {error && (
                <div className={styles.errorBanner}>
                    {error}
                </div>
            )}

            <div className={styles.quickStats}>
                <div className={styles.quickStat}>
                    <div className={`${styles.quickStatIcon} ${styles.quickStatIconInfo}`}>
                        <Users size={18} />
                    </div>
                    <div>
                        <div className={styles.quickStatValue}>{loading ? '—' : admins.length}</div>
                        <div className={styles.quickStatLabel}>Total admins</div>
                    </div>
                </div>
                <div className={styles.quickStat}>
                    <div className={`${styles.quickStatIcon} ${styles.quickStatIconSuccess}`}>
                        <UserCheck size={18} />
                    </div>
                    <div>
                        <div className={styles.quickStatValue}>{loading ? '—' : activeCount}</div>
                        <div className={styles.quickStatLabel}>Active</div>
                    </div>
                </div>
                <div className={styles.quickStat}>
                    <div className={`${styles.quickStatIcon} ${styles.quickStatIconError}`}>
                        <UserX size={18} />
                    </div>
                    <div>
                        <div className={styles.quickStatValue}>{loading ? '—' : expiredCount}</div>
                        <div className={styles.quickStatLabel}>Expired</div>
                    </div>
                </div>
                <div className={styles.quickStat}>
                    <div className={`${styles.quickStatIcon} ${styles.quickStatIconPrimary}`}>
                        <Shield size={18} />
                    </div>
                    <div>
                        <div className={styles.quickStatValue}>You</div>
                        <div className={styles.quickStatLabel}>Superadmin</div>
                    </div>
                </div>
            </div>

            <section className={styles.section}>
                <div className={styles.sectionHeader}>
                    <div>
                        <h2 className={styles.sectionTitle}>Admins</h2>
                        <p className={styles.sectionHint}>
                            Pending signups are approved from the Dashboard. Here you can review and manage existing admins.
                        </p>
                    </div>
                </div>
                {loading ? (
                    <div className={styles.loadingBlock}>
                        <Loader2 size={24} className={styles.spin} />
                        <span>Loading admins…</span>
                    </div>
                ) : (
                    <Table
                        columns={userColumns}
                        rows={admins}
                        emptyMessage="No admins yet. Approve pending signups from the Dashboard to add admins here."
                    />
                )}
            </section>

            {openMenuId && dropdownPosition && (() => {
                const row = admins.find((a) => a.id === openMenuId);
                if (!row) return null;
                return createPortal(
                    <div
                        ref={menuRef}
                        className={styles.actionsDropdown}
                        role="menu"
                        style={{
                            position: 'fixed',
                            top: dropdownPosition.top,
                            left: dropdownPosition.left,
                            zIndex: 9999,
                            minWidth: 180,
                        }}
                    >
                        <button
                            type="button"
                            className={styles.actionsDropdownItem}
                            role="menuitem"
                            onClick={() => { openSummaryModal(row); setOpenMenuId(null); }}
                            disabled={summaryLoading && summaryModal?.id === row.id}
                        >
                            {summaryLoading && summaryModal?.id === row.id ? <Loader2 size={14} className={styles.spin} /> : <FileText size={14} />}
                            <span>Summary</span>
                        </button>
                        <button
                            type="button"
                            className={styles.actionsDropdownItem}
                            role="menuitem"
                            onClick={() => { openResetPwdModal(row); setOpenMenuId(null); }}
                        >
                            <KeyRound size={14} />
                            <span>Reset password</span>
                        </button>
                        <button
                            type="button"
                            className={styles.actionsDropdownItem}
                            role="menuitem"
                            onClick={() => { openExpiryModal(row); setOpenMenuId(null); }}
                        >
                            <CalendarClock size={14} />
                            <span>Set expiry</span>
                        </button>
                        {row.status === 'active' ? (
                            <button
                                type="button"
                                className={styles.actionsDropdownItem}
                                role="menuitem"
                                onClick={() => { handleDeactivate(row.id); setOpenMenuId(null); }}
                                disabled={actionId === row.id}
                            >
                                {actionId === row.id ? <Loader2 size={14} className={styles.spin} /> : <PauseCircle size={14} />}
                                <span>{actionId === row.id ? '…' : 'Deactivate'}</span>
                            </button>
                        ) : (
                            <button
                                type="button"
                                className={`${styles.actionsDropdownItem} ${styles.actionsDropdownItemPositive}`}
                                role="menuitem"
                                onClick={() => { handleActivate(row.id); setOpenMenuId(null); }}
                                disabled={actionId === row.id}
                            >
                                {actionId === row.id ? <Loader2 size={14} className={styles.spin} /> : <PlayCircle size={14} />}
                                <span>{actionId === row.id ? '…' : 'Activate'}</span>
                            </button>
                        )}
                        <button
                            type="button"
                            className={`${styles.actionsDropdownItem} ${styles.actionsDropdownItemDanger}`}
                            role="menuitem"
                            onClick={() => { openDeleteModal(row); setOpenMenuId(null); }}
                            disabled={deletingId === row.id}
                        >
                            {deletingId === row.id ? <Loader2 size={14} className={styles.spin} /> : <Trash2 size={14} />}
                            <span>{deletingId === row.id ? '…' : 'Delete'}</span>
                        </button>
                    </div>,
                    document.body
                );
            })()}

            {summaryModal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.summaryModal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.deleteModalHeader}>
                            <h3 className={styles.deleteModalTitle}>
                                <FileText size={20} className={styles.summaryTitleIcon} />
                                Admin summary: {summaryModal.name}
                            </h3>
                            <button type="button" className={styles.modalClose} onClick={closeSummaryModal} aria-label="Close">
                                <X size={20} />
                            </button>
                        </div>
                        {summaryLoading ? (
                            <div className={styles.loadingBlock}>
                                <Loader2 size={24} className={styles.spin} />
                                Loading summary…
                            </div>
                        ) : summaryData?.error ? (
                            <p className={styles.deleteError}>{summaryData.error}</p>
                        ) : summaryData ? (
                            <div className={styles.summaryBody}>
                                <div className={styles.summaryStats}>
                                    <div className={styles.summaryStat}>
                                        <Coins size={18} />
                                        <span><strong>Coins:</strong> {summaryData.coins ?? 0}</span>
                                    </div>
                                    <div className={styles.summaryStat}>
                                        <span><strong>Subscription:</strong> {summaryData.subscription_end_date ? formatDate(summaryData.subscription_end_date) : '—'}</span>
                                    </div>
                                    <div className={styles.summaryStat}>
                                        <span><strong>Events:</strong> {summaryData.total_events ?? 0} total ({summaryData.active_events ?? 0} active, {summaryData.upcoming_events ?? 0} upcoming, {summaryData.past_events ?? 0} past)</span>
                                    </div>
                                    <div className={styles.summaryStat}>
                                        <span><strong>Total polls:</strong> {summaryData.total_polls ?? 0}</span>
                                    </div>
                                </div>
                                {summaryData.events?.length > 0 && (
                                    <div className={styles.summaryEventsWrap}>
                                        <h4 className={styles.summaryEventsTitle}>Events</h4>
                                        <div className={styles.summaryEventsTableWrap}>
                                            <table className={styles.summaryEventsTable}>
                                                <thead>
                                                    <tr>
                                                        <th>Name</th>
                                                        <th>Code</th>
                                                        <th>Start</th>
                                                        <th>End</th>
                                                        <th>Status</th>
                                                        <th>Polls</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {summaryData.events.map((ev) => (
                                                        <tr key={ev.id}>
                                                            <td>{ev.name}</td>
                                                            <td><code className={styles.eventCode}>{ev.code}</code></td>
                                                            <td>{formatDate(ev.start_date)}</td>
                                                            <td>{formatDate(ev.end_date)}</td>
                                                            <td><StatusBadge status={ev.status} /></td>
                                                            <td>{ev.poll_count ?? 0}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : null}
                        <div className={styles.deleteModalFooter} style={{ marginTop: 'var(--space-4)' }}>
                            <Button variant="secondary" onClick={closeSummaryModal}>Close</Button>
                        </div>
                    </div>
                </div>
            )}

            {resetPwdModal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.deleteModal} onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400 }}>
                        <div className={styles.deleteModalHeader}>
                            <h3 className={styles.deleteModalTitle}>
                                <KeyRound size={20} className={styles.summaryTitleIcon} />
                                Reset password: {resetPwdModal.name}
                            </h3>
                            <button type="button" className={styles.modalClose} onClick={closeResetPwdModal} aria-label="Close">
                                <X size={20} />
                            </button>
                        </div>
                        <p className={styles.deleteModalBody}>Set a new password for this admin (min 8 characters).</p>
                        <input
                            type="password"
                            className={styles.deletePasswordInput}
                            placeholder="New password"
                            value={resetNewPassword}
                            onChange={(e) => { setResetNewPassword(e.target.value); setResetError(''); }}
                            autoFocus
                        />
                        <input
                            type="password"
                            className={styles.deletePasswordInput}
                            placeholder="Confirm password"
                            value={resetConfirmPassword}
                            onChange={(e) => { setResetConfirmPassword(e.target.value); setResetError(''); }}
                            style={{ marginTop: 0 }}
                        />
                        {resetError && <p className={styles.deleteError}>{resetError}</p>}
                        <div className={styles.deleteModalFooter}>
                            <Button variant="secondary" onClick={closeResetPwdModal}>Cancel</Button>
                            <Button
                                variant="primary"
                                onClick={handleResetPassword}
                                disabled={resettingId !== null || resetNewPassword.length < 8 || resetNewPassword !== resetConfirmPassword}
                                icon={resettingId !== null ? <Loader2 size={16} className={styles.spin} /> : <KeyRound size={16} />}
                            >
                                {resettingId !== null ? 'Resetting…' : 'Reset password'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {expiryModal && (
                <div className={styles.modalOverlay} onClick={closeExpiryModal}>
                    <div className={styles.deleteModal} onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400 }}>
                        <div className={styles.deleteModalHeader}>
                            <h3 className={styles.deleteModalTitle}>
                                <CalendarClock size={20} className={styles.summaryTitleIcon} />
                                Set expiry: {expiryModal.name}
                            </h3>
                            <button type="button" className={styles.modalClose} onClick={closeExpiryModal} aria-label="Close">
                                <X size={20} />
                            </button>
                        </div>
                        <p className={styles.deleteModalBody}>
                            Set an expiry date for this admin. After this date, their access will expire. Leave empty for no expiry.
                        </p>
                        <label className={styles.expiryLabel} htmlFor="expiry-date-input">
                            Expiry date
                        </label>
                        <DatePicker
                            id="expiry-date-input"
                            value={expiryDate}
                            onChange={(iso) => { setExpiryDate(iso); setExpiryError(''); }}
                        />
                        <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
                            <button
                                type="button"
                                className={styles.expiryClearBtn}
                                onClick={() => setExpiryDate('')}
                            >
                                Clear expiry
                            </button>
                        </div>
                        {expiryError && <p className={styles.deleteError}>{expiryError}</p>}
                        <div className={styles.deleteModalFooter}>
                            <Button variant="secondary" onClick={closeExpiryModal}>Cancel</Button>
                            <Button
                                variant="primary"
                                onClick={handleSetExpiry}
                                disabled={settingExpiryId !== null}
                                icon={settingExpiryId !== null ? <Loader2 size={16} className={styles.spin} /> : <CalendarClock size={16} />}
                            >
                                {settingExpiryId !== null ? 'Saving…' : 'Save'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {deleteModal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.deleteModal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.deleteModalHeader}>
                            <h3 className={styles.deleteModalTitle}>Permanently delete admin?</h3>
                            <button type="button" className={styles.modalClose} onClick={closeDeleteModal} aria-label="Close">
                                <X size={20} />
                            </button>
                        </div>
                        <p className={styles.deleteModalBody}>
                            This will remove <strong>{deleteModal.name}</strong> ({deleteModal.email}) from the system. This action cannot be undone.
                        </p>
                        <p className={styles.deleteModalHint}>Enter your superadmin password to confirm:</p>
                        <input
                            type="password"
                            className={styles.deletePasswordInput}
                            placeholder="Your password"
                            value={deletePassword}
                            onChange={(e) => { setDeletePassword(e.target.value); setDeleteError(''); }}
                            autoFocus
                        />
                        {deleteError && <p className={styles.deleteError}>{deleteError}</p>}
                        <div className={styles.deleteModalFooter}>
                            <Button variant="secondary" onClick={closeDeleteModal}>Cancel</Button>
                            <Button
                                variant="danger"
                                onClick={handleConfirmDelete}
                                disabled={deletingId !== null || !deletePassword.trim()}
                                icon={deletingId !== null ? <Loader2 size={16} className={styles.spin} /> : <Trash2 size={16} />}
                            >
                                {deletingId !== null ? 'Deleting…' : 'Permanently delete'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPage;
